/**
 * Style des cases d'un tableau du document, tiré de son style (`tableStyle`,
 * cf. `configuration.tableStyles`) : couleurs, zébré, police, bordures,
 * espacement. Les cellules et en-têtes du document s'en servent, l'aperçu du
 * Drawer aussi : il se rend comme le document, par construction.
 *
 * Module partagé document ↔ site : pur, sans React ni store.
 */

// Espacement dans les cellules de la taille « Personnalisée », tant
// qu'aucun n'est saisi.
export const DEFAULT_TABLE_CUSTOM_PADDING = 8;

export const TABLE_PADDING_UNITS = ['px', 'em'];

// Espacement et unité réellement appliqués. Sans valeur saisie, le défaut est
// en px quelle que soit l'unité restée choisie.
export const resolveCustomPadding = ({ customCellPadding, customCellPaddingUnit }) => {
  if (customCellPadding === null || customCellPadding === undefined || customCellPadding === '') {
    return { value: DEFAULT_TABLE_CUSTOM_PADDING, unit: 'px' };
  }
  return {
    value: Number(customCellPadding),
    unit: TABLE_PADDING_UNITS.includes(customCellPaddingUnit) ? customCellPaddingUnit : 'px',
  };
};

// Zébré avec texte alterné, chaque fond a son texte : blanc sur noir, noir sur
// blanc.
export const cellTextColor = (tableStyle, isEven) =>
  tableStyle?.stripped1 && tableStyle?.alternateTextColors && !isEven
    ? tableStyle.customTextColor2?.style
    : tableStyle?.customTextColor1?.style;

// Épaisseur des bordures tant que le style n'en choisit pas une, en px.
export const DEFAULT_TABLE_BORDER_WIDTH = 2;

export const tableBorderStyle = (tableProperties, { isLastColumn = false }) => {
  const hasBorders =
    tableProperties.bordered && tableProperties.customBorderColor;
  const width = Number(tableProperties.customBorderWidth) || DEFAULT_TABLE_BORDER_WIDTH;
  // Traits horizontaux seuls : ni séparations de colonnes, ni bords latéraux.
  const verticalWidth = tableProperties.borderHorizontalOnly ? 0 : width;
  return {
    borderWidth: width,
    ...(tableProperties.borderHorizontalOnly && { borderLeftWidth: 0 }),
    borderRightWidth: isLastColumn ? verticalWidth : 0,
    borderBottomWidth: width,
    borderStyle: tableProperties.customBorderType || 'solid',
    borderColor: hasBorders
      ? tableProperties.customBorderColor?.style
      : 'transparent',
  };
};

// `customPadding` et `customUnit` : espacement saisi pour la taille
// « Personnalisée », et son unité (px ou em).
export const convertPadding = (size, customPadding, customUnit) => {
  let paddingInfos = {};
  let unit = 'px';
  if (size === 'custom') {
    const resolved = resolveCustomPadding({
      customCellPadding: customPadding,
      customCellPaddingUnit: customUnit,
    });
    unit = resolved.unit;
    paddingInfos = { horizontal: resolved.value, vertical: resolved.value };
  } else if (size === 'middle') {
    paddingInfos = { horizontal: 8, vertical: 8 };
  } else if (size === 'small') {
    paddingInfos = { horizontal: 5, vertical: 4 };
  } else {
    paddingInfos = { horizontal: 12, vertical: 12 };
  }
  paddingInfos.padding = `${paddingInfos.vertical}${unit} ${paddingInfos.horizontal}${unit}`;
  // Hauteur plancher des cellules et en-têtes. Une taille personnalisée n'en a
  // pas : à 1px d'espacement, les 32px garderaient une ligne haute, le texte
  // flottant au milieu.
  paddingInfos.minHeight = size === 'custom' ? 0 : 32;
  return paddingInfos;
};

export const getCellStyle = ({
  hidden,
  tableStyle,
  isEven,
  configuration,
  columnKey,
  width,
  isLastColumn,
}) => {
  return {
    display: hidden ? 'none' : undefined,
    position: 'relative',
    padding: 0,
    backgroundColor: tableStyle?.stripped1
      ? isEven
        ? tableStyle.strippedCustomClr1.style
        : tableStyle.strippedCustomClr2.style
      : tableStyle.customCellColor.style,
    color: cellTextColor(tableStyle, isEven),
    fontSize: tableStyle?.fontSizeCells || configuration.fontSize,
    fontFamily: tableStyle?.fontFamilyCells || configuration.fontFamily,
    overflowWrap: 'break-word',
    width: width ?? `calc(var(--${columnKey}-size) * 1px - 1px)`,
    ...tableBorderStyle(tableStyle, { isLastColumn }),
  };
};

export const getHeaderStyle = ({
  tableStyle,
  configuration,
  columnKey,
  width,
  isLastColumn,
}) => {
  return {
    backgroundColor: tableStyle.customHeaderColor.style ?? '#fafafa',
    color: tableStyle.customHeaderTextColor.style,
    fontSize: tableStyle?.fontSizeHeader || configuration.fontSize,
    fontFamily: tableStyle?.fontFamilyHeader || configuration.fontFamily,
    overflowWrap: 'break-word',
    position: 'relative',
    padding: 0,
    ...tableBorderStyle(tableStyle, { isLastColumn }),
  };
};
